import React, { useEffect, useState } from 'react';
import { render } from 'react-dom';
import { Button } from '../../atoms/button';
import styles from './PayPalButton.scss';

const MUTATION_TYPE_TO_CHECK = 'childList';
const MUTATION_BUTTON_CLASS_NAME = 'field';
const MUTATION_DONATION_FORM_NAME = 'donateformapp';

interface PayPalButtonProps {
    charityKey: string;
}

const PayPalButton = function PayPalButton({ charityKey }: PayPalButtonProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isHidden, setIsHidden] = useState(false);

    const handleClick = async () => {
        // Grabbing input values from donation form
        const inputFields = document.querySelectorAll('#donate-form-fields input, #donate-form-fields select, #donate-form-fields textarea');
        const fields: {[key in string]: string | undefined} = {};

        const mapValues = (elementField: Element) => {
            const field = elementField as HTMLInputElement;
            let { name } = field;
            let inputValue = '';

            switch (field.nodeName.toLowerCase()) {
            case 'textarea':
                // Special case for comment/message on form
                if (name === 'message') {
                    name = 'comment_message'; // urgh - must be legacy logic
                }
                inputValue = field.value;
                break;
            case 'input':
                // Special case for amount with matched multiplier
                if (name === 'amount' && field.hasAttribute('data-multiplier')) {
                    fields.multiplier = field.attributes.getNamedItem('data-multiplier')?.value;
                }
                inputValue = field.value;
                break;
            case 'select':
                const selectField = elementField as HTMLSelectElement;
                inputValue = selectField.options[selectField.selectedIndex].text;
                break;
            default:
                break;
            }

            fields[name] = inputValue;
        };

        // Map all input elements
        inputFields.forEach((field) => mapValues(field as HTMLInputElement));

        setIsLoading(true);
        try {
            const tagsRes = await fetch(`/odata/v1.0/records?charityKey=${charityKey}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
                method: 'POST',
                credentials: 'include',
                body: JSON.stringify(fields),
            });

            const { compounduuid } = await tagsRes.json();
            const orderRes = await fetch('/Services/PayPalCommerce/Order', {
                headers: {
                    'Content-Type': 'application/json',
                },
                method: 'POST',
                credentials: 'include',
                body: JSON.stringify(compounduuid),
            });
            if (!orderRes.ok) {
                window.location.assign('/Services/PayPalCommerce/Cancel');
                return;
            }

            const approveLink = await orderRes.json();
            window.location.assign(approveLink);
        } catch (e) {
            // do something here
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // the greatest hacks known to man
        // is possible the form is pre-filled so let's handle that here
        setIsDisabled(Boolean(document.querySelector('#donate-form-actions a')?.attributes.getNamedItem('disabled')));
        const donateButtonObserver = new MutationObserver((mutations) => {
            // we set a data-disabled attribute on the paypalbutton container to know if these buttons are disabled or not
            if (mutations[0].attributeName !== 'data-disabled') {
                return;
            }

            setIsDisabled(!(mutations[0]?.oldValue === 'true'));
        });

        donateButtonObserver.observe(document.querySelector('#paypal-button-container')!, { attributeOldValue: true});

        // handle recurring givings
        // we get the label here because that's the only thing that changes on select
        const onceOffRadio = document.querySelector('#donate-form-fields label[for=giving_frequency_1]');

        // once-off does not exist, hide button
        if (!onceOffRadio) {
            setIsHidden(true);

            return;
        }

        // disable paypal button when we don't have once off selected
        const onceOffObserver = new MutationObserver((mutations) => {
            const target = mutations[0].target as HTMLElement;
            const onceOffValue = target.attributes.getNamedItem('class')?.value;

            setIsHidden(onceOffValue !== 'selected');
        });

        onceOffObserver.observe(onceOffRadio, { attributes: true });

        //  handle mobile since it's a different input
        document.querySelector('#donate-form-fields select[name=giving_frequency]')?.addEventListener('change', (e) => {
            const target = e.target as HTMLSelectElement;
            setIsHidden(target.value !== '1');
        });
    }, []);

    return isHidden ? null : (
        <Button
            id="donate-with-paypal"
            overrideClass={styles['paypal-button']}
            disabled={isLoading || isDisabled}
            onClick={handleClick}
            isLoading={isLoading}
        >
            <div className={styles['paypal-button__content']}>
                <span>Donate with </span>
                <img alt="paypal-logo" className={styles['paypal-button__logo']} src="/images/Icons/paypal-logo.svg" />
            </div>
        </Button>
    );
};

interface PayPayButtonAppProps {
    charitykey: string;
    isteampage: boolean;
}

// render button under the other Donate button in the donation form
const PayPalButtonApp = function PayPalButtonApp({ charitykey, isteampage }: PayPayButtonAppProps) {
    const renderPayPalButton = () => {
        render(<PayPalButton charityKey={charitykey} />, document.querySelector('#paypal-button-container'));
    };

    useEffect(() => {
        if (isteampage) {
            // need the timeout here as I'm not sure what's causing the page to rerender
            setTimeout(renderPayPalButton, 100);
        } else {
            renderPayPalButton();
        }
    }, []);

    return null;
};

export default PayPalButtonApp;
